import { combineLatest, from, map, shareReplay, type Observable } from 'rxjs';
import { DASHBOARD_QUERY_KEYS } from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import type { DashboardQueryKey } from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import type { DashboardQueryResult } from '@h20/contracts/types/api/dashboard/DashboardData';
import type { DashboardDataProvider } from './dashboard-provider.types';

/** Preview data for an authoring board */
export function previewDataProviders(
    registryName: Observable<string | null>
): DashboardDataProvider[] {
    return DASHBOARD_QUERY_KEYS.map((queryKey) => ({
        id: queryKey,
        load: () =>
            combineLatest([fixtureResult(queryKey), registryName]).pipe(
                map(([result, name]) => {
                    // No registry picked yet leaves the fixture's own name, so the line never starts with a blank.
                    if (!name || result.meta === undefined) {
                        return result;
                    }
                    return {
                        ...result,
                        meta: {
                            ...result.meta,
                            scope: { ...result.meta.scope, registryName: name },
                        },
                    };
                })
            ),
    }));
}

/** Loaded once per key and replayed, so a board with several bound cells fetches the chunk once. */
const resultByKey = new Map<DashboardQueryKey, Observable<DashboardQueryResult>>();

function fixtureResult(queryKey: DashboardQueryKey): Observable<DashboardQueryResult> {
    const cached = resultByKey.get(queryKey);
    if (cached) {
        return cached;
    }

    const result = from(import('./dashboard-query-fixtures.data')).pipe(
        map((module) => module.DASHBOARD_QUERY_FIXTURES[queryKey]),
        shareReplay(1)
    );
    resultByKey.set(queryKey, result);
    return result;
}
